import React from 'react'
import { formatManualVersion } from '@common/utils/formatManualVersion'

// The generated table is printed with Roboto over the system sans-serif, so
// the preview asks for the same stack. Every line of the header has to carry
// it: a line left without it falls back to the page font and stops looking
// like the table it previews.
const HEADER_FONT_FAMILY = "'Roboto', sans-serif"

interface Props {
  clubName?: string
  courseName?: string
  ratingName?: string
  holeCount?: number
  holeRange?: string
  totalPar?: number
  revision?: number
  manualVersion?: string
  ratingDate?: string
  isDraft?: boolean
  gender?: string
  hideEmptyValues?: boolean
}

const SlopeTableHeaderPreview = ({
  clubName,
  courseName,
  ratingName,
  holeCount,
  holeRange: holeRangeProp,
  totalPar,
  revision,
  manualVersion,
  ratingDate,
  isDraft,
  gender,
  hideEmptyValues,
}: Props) => {
  // The generated table says which holes it covers, so the preview has to say
  // it too, in the same place. A hand-entered table picks its own range, so it
  // passes one in; everything else runs from the first hole to the last.
  const holeRange =
    holeRangeProp ?? (holeCount && holeCount > 0 ? `1-${holeCount}` : undefined)

  // A preview of a table that is not generated yet leaves missing values out
  // instead of marking them with a dash.
  const placeholder = hideEmptyValues ? undefined : '—'
  const clubLine = clubName || placeholder
  // The generated table holds the course row open when there is no course
  // name, so the preview does the same and keeps the rest of the header in
  // the same place either way.
  const courseLine = courseName || placeholder
  const parValue = totalPar ?? placeholder
  const ratingLine = [
    ratingName || placeholder,
    holeRange ? `Hål ${holeRange}` : undefined,
    gender || '(Herrar/damer)',
    parValue === undefined ? undefined : `Par ${parValue}`,
  ].filter(Boolean)

  return (
    <div style={{ display: 'inline-block' }}>
      <div
        style={{
          marginTop: 12,
          border: '1px solid #ccc',
          borderRadius: 4,
          padding: '8px 12px',
          background: '#ffffff',
          minWidth: 340,
          lineHeight: 1.6,
          textAlign: 'center',
        }}
      >
        {clubLine && (
          <div
            style={{
              fontFamily: HEADER_FONT_FAMILY,
              fontSize: 20,
              fontWeight: 900,
              textTransform: 'uppercase',
            }}
          >
            {clubLine}
          </div>
        )}
        <div
          style={{
            fontFamily: HEADER_FONT_FAMILY,
            fontSize: 12,
            fontWeight: 700,
            lineHeight: '14px',
            minHeight: 14,
          }}
        >
          {courseLine}
        </div>
        <div style={{ borderBottom: '1px solid black', margin: '4px 0' }} />
        <div
          style={{
            fontFamily: HEADER_FONT_FAMILY,
            fontSize: 14,
            fontWeight: 500,
            textTransform: 'capitalize',
          }}
        >
          {isDraft && '(DRAFT) '}
          {ratingLine.join(' • ')}
        </div>
        <div
          style={{
            fontFamily: HEADER_FONT_FAMILY,
            fontSize: 10,
            fontWeight: 500,
          }}
        >
          {isDraft ? 'Tabell genererad ' : 'Banan värderad '}
          {isDraft
            ? new Date().toLocaleDateString('sv-SE')
            : ratingDate
            ? new Date(ratingDate).toLocaleDateString('sv-SE')
            : '####-##-##'}
          {revision ? ` • Rev ${revision}` : ''}
          {isDraft && manualVersion
            ? ` • WHS: ${formatManualVersion(manualVersion)}`
            : ''}
        </div>
      </div>
    </div>
  )
}

export default SlopeTableHeaderPreview
